import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Instagram, Menu, X, Info, Star, Quote } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

/*
  Pilot redesign — Blacano Dental Center's own concept website.
  Bespoke composition (not the shared DentalSite template): warm gold/cream
  premium-clinical identity, first-person clinic voice, editorial layout.
*/

const GOLD = "#C5A059";
const GOLD_DARK = "#8E7036";
const INK = "#1A1A1A";
const CREAM = "#FDFBF7";
const BEIGE = "#F5EFE6";

const FB = "https://www.facebook.com/Bdc.ilo/";
const IG = "https://www.instagram.com/bdciloilo/";
const NAME = "Blacano Dental Center";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function BlacanoConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Our Clinic", "#about"], ["Services", "#services"], ["Our Dentist", "#dentist"], ["Reviews", "#reviews"], ["Contact", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="blacano-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600&family=Manrope:wght@300;400;500;600;700&display=swap');
        .bdc-serif { font-family: 'Playfair Display', serif; }
      `}</style>

      {/* MYSITECARE CONCEPT DISCLAIMER — kept visually separate from the clinic site */}
      <div data-testid="dental-disclaimer" className="bg-gray-900 text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2">
          <Info size={13} className="shrink-0 text-white/70" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      {/* HEADER */}
      <header className={`sticky top-0 z-40 transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.06)]" : ""}`} style={{ background: CREAM, borderBottom: `1px solid #E6D5B8` }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="bdc-logo" className="flex items-center gap-3">
            <img src="/dental/blacano-dental-concept/logo.jpg" alt={NAME} className="h-11 w-11 rounded-full object-cover" style={{ boxShadow: `0 0 0 2px ${GOLD}` }} />
            <span className="bdc-serif text-lg font-medium tracking-tight">{NAME}</span>
          </a>
          <div className="hidden lg:flex items-center gap-9">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-medium text-[#5C564D] hover:text-[#1A1A1A] transition-colors">{l}</a>
            ))}
          </div>
          <a href="#visit" data-testid="bdc-header-cta" className="hidden sm:inline-flex rounded-sm px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: GOLD }}>
            Book an Appointment
          </a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="bdc-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: CREAM }}>
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-[#2A2520] font-medium">{l}</a>)}
            <a href="#visit" onClick={() => setOpen(false)} data-testid="bdc-mobile-cta" className="block mt-2 text-center rounded-sm px-5 py-3 text-sm font-semibold text-white" style={{ background: GOLD }}>Book an Appointment</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[82vh] flex items-end" data-testid="bdc-hero">
        <img src="/dental/blacano-dental-concept/intraoral-cam.jpg" alt="Chairside care at Blacano Dental Center" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(26,26,26,0.08) 0%, rgba(26,26,26,0.36) 62%, rgba(26,26,26,0.7) 100%)` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>La Paz, Iloilo City</span>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] text-white bdc-serif">{NAME}</h1>
            <p className="mt-5 text-lg text-white/85 font-light leading-relaxed max-w-lg">
              Modern dental care built around comfort, clear communication, and precise orthodontic and restorative work — right here in La Paz.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#visit" data-testid="bdc-hero-book" className="rounded-sm px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: GOLD }}>Book an Appointment</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="bdc-hero-contact" className="rounded-sm border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">Contact Us</a>
            </div>
            <p className="mt-7 text-sm text-white/70 font-light" data-testid="bdc-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: GOLD }} /> 5.0 Google Rating · 46 Reviews · La Paz, Iloilo City
            </p>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <motion.div {...fadeUp} className="md:col-span-5">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>About Us</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-medium leading-snug bdc-serif">Our Clinic</h2>
        </motion.div>
        <motion.div {...fadeUp} className="md:col-span-7">
          <p className="text-lg leading-relaxed text-[#2A2520]/85 font-light">
            At {NAME}, we believe patients deserve to understand their treatment and feel genuinely comfortable throughout every visit. Our team is known for a gentle, patient-first approach — taking the time to explain each procedure clearly, whether it's a routine check-up, an orthodontic case, or a more involved restorative treatment.
          </p>
          <p className="mt-5 text-lg leading-relaxed text-[#2A2520]/85 font-light">
            We're a modern, well-equipped practice serving La Paz, Iloilo City, and we follow up with our patients after treatment — because dental care doesn't end when you leave the chair.
          </p>
          <div className="mt-8 flex items-center gap-8 border-t pt-6" style={{ borderColor: "#E6D5B8" }}>
            <div>
              <div className="text-3xl font-medium bdc-serif" style={{ color: GOLD_DARK }}>5.0★</div>
              <div className="text-xs text-[#5C564D] mt-1">46 Google reviews</div>
            </div>
            <div>
              <div className="text-3xl font-medium bdc-serif" style={{ color: GOLD_DARK }}>100%</div>
              <div className="text-xs text-[#5C564D] mt-1">Recommend on Facebook</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 md:py-28" style={{ background: BEIGE }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>What We Offer</span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-medium leading-snug bdc-serif">Our Dental Services</h2>
          </motion.div>

          <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-0 md:gap-16 items-center mb-16 md:mb-24">
            <div className="order-2 md:order-1">
              <span className="bdc-serif text-2xl" style={{ color: GOLD_DARK }}>01</span>
              <h3 className="mt-2 text-2xl sm:text-3xl font-medium bdc-serif">Orthodontics &amp; TAD-Assisted Correction</h3>
              <p className="mt-4 text-base leading-relaxed text-[#2A2520]/80 font-light">
                Braces and orthodontic treatment, including temporary anchorage device (TAD) cases for overjet correction — the kind of precise, well-planned care our patients specifically seek us out for.
              </p>
            </div>
            <div className="order-1 md:order-2 rounded-sm overflow-hidden" style={{ boxShadow: "0 8px 30px rgba(197,160,89,0.15)" }}>
              <img src="/dental/blacano-dental-concept/intraoral-cam.jpg" alt="Orthodontic and TAD-assisted care" className="w-full h-72 md:h-96 object-cover" />
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-10 border-t pt-14" style={{ borderColor: "#E6D5B8" }}>
            <motion.div {...fadeUp}>
              <span className="bdc-serif text-xl" style={{ color: GOLD_DARK }}>02</span>
              <h3 className="mt-2 text-xl font-medium bdc-serif">General Dentistry</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#2A2520]/75 font-light">Routine check-ups, cleanings and preventive care to keep your smile healthy year-round.</p>
            </motion.div>
            <motion.div {...fadeUp}>
              <span className="bdc-serif text-xl" style={{ color: GOLD_DARK }}>03</span>
              <h3 className="mt-2 text-xl font-medium bdc-serif">Cosmetic Dentistry</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#2A2520]/75 font-light">Treatments focused on the health and appearance of your smile, planned around your goals.</p>
            </motion.div>
            <motion.div {...fadeUp}>
              <span className="bdc-serif text-xl" style={{ color: GOLD_DARK }}>04</span>
              <h3 className="mt-2 text-xl font-medium bdc-serif">Restorative Dentistry</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#2A2520]/75 font-light">Root canal therapy and crown work designed to save and preserve your natural teeth wherever possible.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DENTIST SPOTLIGHT */}
      <section id="dentist" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
        <motion.div {...fadeUp} className="rounded-sm overflow-hidden order-1" style={{ boxShadow: "0 8px 30px rgba(197,160,89,0.15)" }}>
          <img src="/dental/blacano-dental-concept/doctor.jpg" alt="Dr. Kyra Jean Blacano" className="w-full h-[420px] sm:h-[480px] object-cover" />
        </motion.div>
        <motion.div {...fadeUp} className="order-2">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Meet Our Chief Dentist</span>
          <h2 className="mt-3 text-4xl font-medium bdc-serif">Dr. Kyra Jean Blacano</h2>
          <p className="mt-1 text-sm font-medium tracking-wide" style={{ color: GOLD_DARK }}>CHIEF DENTIST</p>
          <p className="mt-5 text-lg leading-relaxed text-[#2A2520]/85 font-light">
            Patients consistently describe Dr. Kyra as kind, gentle, and clear in explaining treatment — especially praised for orthodontic and TAD cases, and for personally following up with patients by SMS after procedures.
          </p>
        </motion.div>
      </section>

      {/* TREATMENTS / SMILE GALLERY */}
      <section className="py-20 md:py-28" style={{ background: BEIGE }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Real Results</span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-medium leading-snug bdc-serif">Our Work</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { img: "/dental/blacano-dental-concept/ortho.jpg", label: "Braces with TADs — overjet correction" },
              { img: "/dental/blacano-dental-concept/aesthetic-restoration.jpg", label: "Aesthetic restoration case" },
            ].map((item) => (
              <motion.div key={item.img} {...fadeUp} data-testid="bdc-gallery-item" className="bg-white rounded-sm overflow-hidden border" style={{ borderColor: "#E6D5B8", boxShadow: "0 8px 30px rgba(197,160,89,0.1)" }}>
                <img src={item.img} alt={item.label} className="w-full object-contain" loading="lazy" />
                <p className="px-5 py-4 text-sm text-[#5C564D]">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Patient Experience</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-medium leading-snug bdc-serif">What Our Patients Say</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-10">
          {[
            { text: "Staff were approachable, welcoming and professional. Dr. Kyra was kind and gentle, explained everything clearly, and I felt very good after my surgery — highly recommended.", author: "Ma. Laika Calizo" },
            { text: "Another dentist recommended extraction, but Dr. Kyra saved the tooth with a root canal and crown instead — worked out great. Modern, well-equipped, professional, and they even texted afterward to check on me.", author: "Shaira Kim Llanora" },
            { text: "Friendly staff, Dr. Kyra's patience and skill stood out — procedures were explained thoroughly and treatment was painless and efficient.", author: "Waltz" },
          ].map((q, i) => (
            <motion.div key={i} {...fadeUp} data-testid={`bdc-testimonial-${i}`}>
              <Quote size={28} style={{ color: GOLD }} className="mb-3" />
              <p className="text-base leading-relaxed text-[#2A2520]/85 font-light italic">"{q.text}"</p>
              <p className="mt-4 text-sm font-semibold" style={{ color: GOLD_DARK }}>{q.author}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* APPOINTMENT CTA */}
      <section className="py-20 md:py-24 text-center" style={{ background: GOLD_DARK }}>
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="text-3xl sm:text-4xl font-medium text-white bdc-serif">Ready to schedule your visit?</h2>
          <p className="mt-4 text-white/80 font-light">Reach out and our team will help you find a time that works.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="tel:+639811705118" data-testid="bdc-cta-call" className="rounded-sm px-7 py-3.5 text-sm font-semibold bg-white transition-transform hover:-translate-y-0.5" style={{ color: GOLD_DARK }}>Call the Clinic</a>
            <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="bdc-cta-message" className="rounded-sm border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">Message Us</a>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section id="visit" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Visit Us</span>
          <h2 className="mt-3 text-4xl font-medium bdc-serif">Find {NAME}</h2>
          <ul className="mt-8 space-y-6">
            <li className="flex items-start gap-4">
              <MapPin size={20} style={{ color: GOLD_DARK }} className="mt-0.5 shrink-0" />
              <div><div className="text-xs uppercase tracking-wide text-[#5C564D]">Address</div><div className="text-[#1A1A1A]">Corner Burgos &amp; Magdalo St., La Paz, Iloilo City 5000</div></div>
            </li>
            <li className="flex items-start gap-4">
              <Phone size={20} style={{ color: GOLD_DARK }} className="mt-0.5 shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wide text-[#5C564D]">Phone</div>
                <a href="tel:+639811705118" className="text-[#1A1A1A] hover:underline">+63 981 170 5118</a>
                <span className="block text-sm text-[#5C564D]">(033) 330-2879</span>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Facebook size={20} style={{ color: GOLD_DARK }} className="mt-0.5 shrink-0" />
              <div><div className="text-xs uppercase tracking-wide text-[#5C564D]">Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="bdc-facebook-link" className="text-[#1A1A1A] hover:underline">facebook.com/Bdc.ilo</a></div>
            </li>
            <li className="flex items-start gap-4">
              <Instagram size={20} style={{ color: GOLD_DARK }} className="mt-0.5 shrink-0" />
              <div><div className="text-xs uppercase tracking-wide text-[#5C564D]">Instagram</div><a href={IG} target="_blank" rel="noopener noreferrer" data-testid="bdc-instagram-link" className="text-[#1A1A1A] hover:underline">instagram.com/bdciloilo</a></div>
            </li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="rounded-sm overflow-hidden border min-h-[320px]" style={{ borderColor: "#E6D5B8" }}>
          <iframe title={`${NAME} location map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Burgos Magdalo St La Paz Iloilo City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: "#1A1A1A", color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src="/dental/blacano-dental-concept/logo.jpg" alt={NAME} className="h-10 w-10 rounded-full object-cover" style={{ boxShadow: `0 0 0 2px ${GOLD}` }} />
              <span className="bdc-serif text-white text-lg">{NAME}</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Modern, well-equipped dental care in La Paz, Iloilo City — orthodontics, general and restorative dentistry.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Services</div>
            <ul className="text-sm space-y-2">
              <li>Orthodontics &amp; TAD-Assisted Correction</li>
              <li>General Dentistry</li>
              <li>Cosmetic Dentistry</li>
              <li>Restorative Dentistry</li>
            </ul>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Corner Burgos &amp; Magdalo St.,<br />La Paz, Iloilo City 5000</p>
            <p className="text-sm mt-2">+63 981 170 5118</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
              <a href={IG} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Treatment, pricing, and availability should always be confirmed directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-sm px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: GOLD }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug="blacano-dental-concept" />
    </div>
  );
}
